import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PhotoGrid } from '../components/PhotoGrid';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { QRCodeDisplay } from '../components/QRCodeDisplay';
import { useGallery } from '../hooks/useGallery';
import { usePhotos } from '../hooks/usePhotos';
import { PhotoStreamClient } from '../api/PhotoStreamClient';
import { Photo } from '../api/types';

const apiClient = new PhotoStreamClient(
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'
);

export const GalleryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { gallery, loading: galleryLoading, error: galleryError, loadGallery } = useGallery(apiClient);
  const { photos, loading: photosLoading, error: photosError, loadPhotos } = usePhotos(apiClient);

  useEffect(() => {
    if (id) {
      loadGallery(id);
      loadPhotos(id);
    }
  }, [id, loadGallery, loadPhotos]);

  const handlePhotoClick = (photo: Photo) => {
    if (id) {
      navigate(`/gallery/${id}/photo/${photo.id}`);
    }
  };

  const handleRetry = () => {
    if (id) {
      loadGallery(id);
      loadPhotos(id);
    }
  };

  if (!id) {
    return (
      <div className="container mx-auto py-8">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle className="text-destructive">Invalid Gallery</CardTitle>
            <CardDescription>Gallery ID is missing.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/">Return to Home</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="mb-6">
        <Button variant="outline" asChild>
          <Link to="/">← Back to Home</Link>
        </Button>
      </div>

      {galleryError && (
        <Card className="mb-6 border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Error Loading Gallery</CardTitle>
            <CardDescription>{galleryError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleRetry} variant="outline">
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {galleryLoading ? (
        <LoadingSpinner />
      ) : gallery ? (
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">{gallery.name}</h1>
            <p className="text-muted-foreground mb-4">{gallery.description}</p>
            <p className="text-sm text-muted-foreground">
              Created: {new Date(gallery.createdDate).toLocaleDateString()}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3">
              <div className="mb-4">
                <h2 className="text-xl font-semibold">
                  Photos ({photos.length})
                </h2>
              </div>

              {photosError && (
                <Card className="mb-4 border-destructive">
                  <CardContent className="pt-6">
                    <p className="text-destructive text-sm">{photosError}</p>
                  </CardContent>
                </Card>
              )}

              <PhotoGrid 
                photos={photos} 
                loading={photosLoading} 
                apiClient={apiClient}
                onPhotoClick={handlePhotoClick}
              />
            </div>

            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle>Upload Photos</CardTitle>
                  <CardDescription>
                    Scan QR code with your phone or use the upload button
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-center">
                    <QRCodeDisplay galleryId={id} />
                  </div>
                  <Button asChild className="w-full">
                    <Link to={`/gallery/${id}/upload`}>Upload Photos</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      ) : (
        !galleryError && (
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle>Gallery Not Found</CardTitle>
              <CardDescription>
                The gallery you're looking for doesn't exist.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <Link to="/">Return to Home</Link>
              </Button>
            </CardContent>
          </Card>
        )
      )}
    </div>
  );
};
